import { Button, DropdownMenu, TableItem } from 'components/atoms';
import { FormInput, MagellanPopups } from 'components/molecules';
import LabeledReportStatus from 'components/molecules/labeledStatus/LabeledReportStatus';
import { Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { FormikHelpers } from 'formik';
import { useComponentVisible, useCountdown, useHasPermission } from 'hooks';
import { useInfiniteTable } from 'hooks/useInfiniteTable';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import i18next from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiNavigationArrow } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { PopupActions } from 'reactjs-popup/dist/types';
import {
  useCancelLabelPeriodReport,
  useCheckLabelReportPeriodsMutation,
  useGetLabelReportsPeriodOrders,
  usePostLabelPeriodReport,
  usePostPeriodReport,
} from 'services/label-report';
import {
  ILabelReportMutationForm,
  IPeriodReportMutationForm,
  REPORT_GENERATION_STATUS,
} from 'services/label-report/types';
import { useGetLabelLightList } from 'services/labels/light';
import { useGetSettlementLightList } from 'services/settlementPeriods';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { formatDate, formatDateToInput } from 'utils/global/date.utils';
import { getSettlementPeriodName } from 'utils/global/name.utils';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';
import { boolean, object, string } from 'yup';

enum LABELREPORTS_COLUMNS {
  label,
  startSettlementPeriod,
  endSettlementPeriod,
  plannedStartDate,
  status,
}

const labelRaportInitialValue: ILabelReportMutationForm = {
  labelId: '',
  startSettlementPeriodId: '',
  endSettlementPeriodId: '',
  planedStartDate: formatDateToInput(new Date(), true),
  asap: true,
};

export const labelReportIndividualValidator = object().shape({
  startSettlementPeriodId: string().required(
    i18next.t('formik-error.required')
  ),
  endSettlementPeriodId: string().required(i18next.t('formik-error.required')),
  planedStartDate: string().when('asap', {
    is: (asap: boolean) => !!asap,
    then: schema => schema.optional(),
    otherwise: schema => schema.required(i18next.t('formik-error.required')),
  }),
  asap: boolean().required(i18next.t('formik-error.required')),
});

const labelReportValidator = labelReportIndividualValidator.shape({
  labelId: string().required(i18next.t('formik-error.required')),
});

const periodReportInitialValue: IPeriodReportMutationForm = {
  settlementPeriodId: '',
  planedStartDate: formatDateToInput(new Date(), true),
  asap: true,
};

const periodReportValidator = object().shape({
  settlementPeriodId: string().required(i18next.t('formik-error.required')),
  planedStartDate: string().when('asap', {
    is: (asap: boolean) => !!asap,
    then: schema => schema.optional(),
    otherwise: schema => schema.required(i18next.t('formik-error.required')),
  }),
  asap: boolean().required(i18next.t('formik-error.required')),
});

const LabelReports = () => {
  usePermissionGuard('LabelReportOrderPermissionsLIST**');
  const hasPermission = useHasPermission();
  const createLabelReport = usePostLabelPeriodReport();
  const getSettelmentPeriod = useGetSettlementLightList();
  const getLabelLightList = useGetLabelLightList();
  const getLabelReports = useGetLabelReportsPeriodOrders();
  const createPeriodReport = usePostPeriodReport();
  const cancelLabelReport = useCancelLabelPeriodReport();
  const checkPeriods = useCheckLabelReportPeriodsMutation();

  const [showWarningPopup, setShowWarningPopup] = useState(false);
  const [reportData, setReportData] = useState<{
    labelId: string;
    startSettlementPeriodId: string;
    endSettlementPeriodId: string;
    planedStartDate: string;
    asap: boolean;
  } | null>(null);

  const tableProps = useInfiniteTable(getLabelReports, { selectable: false });

  const { ref, isComponentVisible, handleComponentVisible } =
    useComponentVisible<HTMLDivElement>(false);

  usePermissionGuard('LabelReportOrderPermissionsLIST**');

  const { t } = useTranslation();
  const navigate = useNavigate();

  const i18nextPrefix = 'label-reports.list';

  const columnsVisibility = useTableColumnsVisibility(
    'LabelReports',
    LABELREPORTS_COLUMNS
  );

  const getLabelOptions = () =>
    getLabelLightList.data &&
    getLabelLightList.data.map(label => ({
      value: label.id,
      label: label.labelName,
    }));

  const getSettlementPeriodOptions = () =>
    getSettelmentPeriod.data?.flat().map(settelment => ({
      value: settelment.id,
      label: getSettlementPeriodName(settelment.settlementPeriodName),
    }));

  const onSubmitByLabel = async (
    values: ILabelReportMutationForm,
    helpers: FormikHelpers<ILabelReportMutationForm>,
    actions: PopupActions | null
  ) => {
    try {
      const checkResult = await checkPeriods.mutateAsync({
        labelId: values.labelId,
        startSettlementPeriodId: values.startSettlementPeriodId,
        endSettlementPeriodId: values.endSettlementPeriodId,
      });

      if (checkResult) {
        setReportData({
          labelId: values.labelId,
          startSettlementPeriodId: values.startSettlementPeriodId,
          endSettlementPeriodId: values.endSettlementPeriodId,
          planedStartDate: values.planedStartDate,
          asap: values.asap,
        });
        setShowWarningPopup(true);
        actions?.close();
        return;
      }

      await createLabelReport.mutateAsync(
        {
          ...values,
          planedStartDate: values.planedStartDate.length
            ? new Date(values.planedStartDate).toISOString()
            : null,
        },
        {
          onSuccess: async () => {
            await getLabelReports.refetch();
            helpers.setValues(labelRaportInitialValue);
            actions?.close();
          },
        }
      );
    } catch (error) {
      if (error instanceof Error) {
        onErrorShowAllErrorsToast(error);
      } else {
        onErrorShowAllErrorsToast(new Error('Unknown error occurred'));
      }
    }
  };

  const onSubmitAll = async (
    values: IPeriodReportMutationForm,
    helpers: FormikHelpers<IPeriodReportMutationForm>,
    actions: PopupActions | null
  ) => {
    await createPeriodReport.mutateAsync(
      {
        ...values,
        planedStartDate: values.planedStartDate.length
          ? new Date(values.planedStartDate).toISOString()
          : null,
      },
      {
        onSuccess: async () => {
          await getLabelReports.refetch();
          helpers.setValues(periodReportInitialValue);
          actions?.close();
        },
      }
    );
  };

  const handleCreateReport = async () => {
    if (reportData) {
      try {
        await createLabelReport.mutateAsync(
          {
            ...reportData,
            planedStartDate: reportData.planedStartDate.length
              ? new Date(reportData.planedStartDate).toISOString()
              : null,
          },
          {
            onSuccess: async () => {
              setShowWarningPopup(false);
              await getLabelReports.refetch();
            },
          }
        );
      } catch (error) {
        if (error instanceof Error) {
          onErrorShowAllErrorsToast(error);
        } else {
          onErrorShowAllErrorsToast(
            new Error('Unknown error occurred while creating report')
          );
        }
      }
    }
  };

  const cancelLabelReportFuncion = async (
    labelPeriodReportOrderId: string,
    actions: PopupActions
  ) => {
    await cancelLabelReport.mutateAsync(
      { labelPeriodReportOrderId },
      {
        onSuccess: async () => {
          await getLabelReports.refetch();
          actions.close();
        },
        onError: async error => {
          onErrorShowAllErrorsToast(error);
          await getLabelReports.refetch();
        },
      }
    );
  };

  const goToLabel = (id: string) => window.open(`/label/${id}/reports`, '_blank');
  const buttonStyle = { justifyContent: 'start' };

  const { countdown, isCountingDown, startCounter } = useCountdown(5, false);

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t(`${i18nextPrefix}.title`)}
          hideBackButton
          topbarContent={
            <>
              <div className='flex gap-2'>
                {/* <Button
                  onlyIcon
                  isClear
                  disabled={isCountingDown}
                  onClick={async () => {
                    startCounter();
                    await getLabelReports.refetch();
                  }}>
                  {isCountingDown && <Button.Text text={`${countdown}`} />}
                  <Button.Icon icon={<PiArrowsCounterClockwise />} />
                </Button> */}
                <Table.CustomColumnsButton
                  columns={LABELREPORTS_COLUMNS}
                  isClear
                  visibility={columnsVisibility}
                  i18nextPrefix='input.label'
                  customI18next=''
                />
                {hasPermission('LabelReportOrderPermissionsLIST**', 1) && (
                  <Button
                    variant={BUTTON_VARIANT.submit}
                    onClick={handleComponentVisible}
                    outsideChildren={
                      <DropdownMenu
                        ref={ref}
                        visible={isComponentVisible}
                        style={{ padding: 5 }}
                        justifyPosition={'end'}>
                        <div className='flex flex-col w-full'>
                          <MagellanPopups.Form
                            initialValues={labelRaportInitialValue}
                            validationSchema={labelReportValidator}
                            onSubmit={onSubmitByLabel}
                            header={t('label-reports.list.popup.header')}
                            trigger={
                              <Button
                                isClear
                                buttonStyle={buttonStyle}
                                fullWidth>
                                <Button.Text
                                  text={t(`label-reports.create.orderByLabel`)}
                                />
                              </Button>
                            }>
                            {({ values }) => (
                              <>
                                <FormInput.Select
                                  label={t('input.label.label')}
                                  name='labelId'
                                  isAsync
                                  options={getLabelOptions()}
                                />
                                <FormInput.Select
                                  label={t('input.label.startSettlementPeriod')}
                                  name='startSettlementPeriodId'
                                  isAsync
                                  options={getSettlementPeriodOptions()}
                                />
                                <FormInput.Select
                                  label={t('input.label.endSettlementPeriod')}
                                  name='endSettlementPeriodId'
                                  isAsync
                                  options={getSettlementPeriodOptions()}
                                />
                                <FormInput.Select
                                  name={`asap`}
                                  label={t('input.label.asap')}
                                  options={[
                                    {
                                      label: t('input.option.yes'),
                                      value: true,
                                    },
                                    {
                                      label: t('input.option.no'),
                                      value: false,
                                    },
                                  ]}
                                />
                                <FormInput.Text
                                  label={t('input.label.plannedStartDate')}
                                  type='datetime-local'
                                  min={formatDateToInput(new Date(), true)}
                                  name='planedStartDate'
                                  disabled={values.asap !== false}
                                />
                              </>
                            )}
                          </MagellanPopups.Form>
                          <MagellanPopups.Form
                            header={t('period-reports.popup.header')}
                            trigger={
                              <Button
                                isClear
                                buttonStyle={buttonStyle}
                                fullWidth>
                                <Button.Text
                                  text={t(`label-reports.create.orderAll`)}
                                />
                              </Button>
                            }
                            initialValues={periodReportInitialValue}
                            validationSchema={periodReportValidator}
                            onSubmit={onSubmitAll}>
                            {({ values }) => (
                              <>
                                <FormInput.Select
                                  label={t('input.label.settlementPeriod')}
                                  name='settlementPeriodId'
                                  options={getSettlementPeriodOptions()}
                                  isAsync
                                />
                                <FormInput.Select
                                  name={`asap`}
                                  label={t('input.label.asap')}
                                  options={[
                                    {
                                      label: t('input.option.yes'),
                                      value: true,
                                    },
                                    {
                                      label: t('input.option.no'),
                                      value: false,
                                    },
                                  ]}
                                />
                                <FormInput.Text
                                  label={t('input.label.plannedStartDate')}
                                  type='datetime-local'
                                  min={formatDateToInput(new Date(), true)}
                                  name='planedStartDate'
                                  disabled={values.asap !== false}
                                />
                              </>
                            )}
                          </MagellanPopups.Form>
                        </div>
                      </DropdownMenu>
                    }>
                    <Button.Text text={t(`label-reports.addOrder`)} />
                  </Button>
                )}
              </div>
            </>
          }></TopBar>
      }>
      <Table
        size='compact'
        id={columnsVisibility.tableId}
        {...tableProps}
        columns={[
          {
            label: t('input.label.label'),
            renderCell: ({ labelName }) => (
              <TableItem>
                <TableItem.Text text={labelName} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(LABELREPORTS_COLUMNS.label),
          },
          {
            label: t('input.label.startSettlementPeriod'),
            renderCell: ({ startSettlementPeriod }) => (
              <TableItem>
                <TableItem.Text
                  text={getSettlementPeriodName(
                    startSettlementPeriod.settlementPeriodName
                  )}
                />
                <TableItem.Text
                  isSupplementary
                  text={
                    startSettlementPeriod.startDate &&
                    formatDate(startSettlementPeriod.startDate)
                  }
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(
              LABELREPORTS_COLUMNS.startSettlementPeriod
            ),
          },

          {
            label: t('input.label.endSettlementPeriod'),
            renderCell: ({ endSettlementPeriod }) => (
              <TableItem>
                <TableItem.Text
                  text={getSettlementPeriodName(
                    endSettlementPeriod.settlementPeriodName
                  )}
                />
                <TableItem.Text
                  isSupplementary
                  text={
                    endSettlementPeriod.endDate &&
                    formatDate(endSettlementPeriod.endDate)
                  }
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(
              LABELREPORTS_COLUMNS.endSettlementPeriod
            ),
          },
          {
            label: t('input.label.plannedStartDate'),
            renderCell: ({ plannedStartDate }) => (
              <TableItem>
                <TableItem.Text text={formatDate(plannedStartDate, true)} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(LABELREPORTS_COLUMNS.plannedStartDate),
          },
          {
            label: t('input.label.status'),
            renderCell: ({ reportGenerationStatusId }) => (
              <TableItem>
                <LabeledReportStatus status={reportGenerationStatusId} />
              </TableItem>
            ),
            hide: columnsVisibility.hide(LABELREPORTS_COLUMNS.status),
          },
          {
            label: '',
            renderCell: ({ id, reportGenerationStatusId, labelId }) => (
              <TableItem justifyPosition='end'>
                <div className='flex gap-2'>
                  {reportGenerationStatusId ===
                    REPORT_GENERATION_STATUS.Waiting && (
                    <MagellanPopups.CancelWarning
                      name={t('label-reports.what')}
                      onCancel={actions =>
                        cancelLabelReportFuncion(id, actions)
                      }
                    />
                  )}
                  <Button
                    type='button'
                    isClear
                    onlyIcon
                    onClick={() => goToLabel(labelId)}>
                    <Button.Icon
                      icon={<PiNavigationArrow transform='scale(-1,1)' />}
                    />
                  </Button>
                </div>
              </TableItem>
            ),
          },
        ]}
      />
      <MagellanPopups.WarningPopup
        open={showWarningPopup}
        onClose={() => setShowWarningPopup(false)}
        onConfirm={handleCreateReport}
        title={t('label-reports.warning.title')}
        message={t('label-reports.warning.message')}
      />
    </TopbarPage>
  );
};

export default LabelReports;
